import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import numeral from "numeral";
import { withStyles } from "@material-ui/core/styles";
import {
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Typography,
  Divider
} from "@material-ui/core";
import { Sparklines, SparklinesLine, SparklinesSpots } from "react-sparklines";
import PageDetailsIcon from "@material-ui/icons/ChevronRight";
import PageActionRecommender from "./PageActionRecommender";
import CategoryTypeIndicator from "./CategoryTypeIndicator";

const runningLocal = process.env.REACT_APP_API_DOMAIN !== "sc";
const ItemService = runningLocal
  ? require("../data/ItemService.dev").default
  : require("../data/ItemService.prod").default;

const popoverHeight = 260;

const styles = theme => ({
  root: {
    zIndex: 1300,
    padding: theme.spacing.unit / 1.5,
    margin: theme.spacing.unit / 1.5,
    marginBottom: 0,
    maxHight: popoverHeight
  },
  container: {
    padding: theme.spacing.unit / 1.5
  },
  detailButtonContainer: {
    paddingLeft: theme.spacing.unit
  },
  trendLabel: {
    paddingTop: theme.spacing.unit / 3,
    textAlign: "right"
  }
});


class NodePopover extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hovered: false,
      contentItem: null,
      recommendations: [],
      loading: true
    };

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
  }

  onMouseEnter() {
    this.setState({ hovered: true });
  }

  onMouseLeave() {
    this.setState({ hovered: false });
  }

  componentDidMount() {
    this._loadPageDetails();
  }

  componentDidUpdate() {
    this._loadPageDetails();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.node !== nextState.node ||
      this.state.loading !== nextState.loading
    );
  }

  _loadPageDetails() {
    const nodeData =
      this.state.hovered && this.state.node ? this.state.node.data : null;
    if (!nodeData) {
      console.warn("popover: no node data");
      return;
    }

    Promise.resolve(ItemService.getItemDataByPath(nodeData.path)).then(item => {
      this.setState({
        loading: false,
        contentItem: item,
        recommendations: PageActionRecommender.getRecommendations(
          nodeData,
          item
        )
      });
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // if hovered, fetching node from previous state
    if (nextProps.node === null && prevState.hovered) {
      return {
        node: prevState.node
      };
    }
    return {
      node: nextProps.node
    };
  }

  getCategoryName(data) {
    const siteRoot = "root page";
    //TODO: this is a test. it only returns the first level for the category
    if (data.path === "/") {
      return siteRoot;
    }

    let parts = data.path.split("/");
    parts.shift();
    if (parts.length === 1) {
      return siteRoot;
    }

    //console.log(parts);
    return parts[0];
  }

  render() {
    const { classes } = this.props;
    const { node, loading, contentItem } = this.state;

    if (!node) {
      return null;
    }

    const { data, params } = node;
    if (!data || !params) {
      return null;
    }

    let personalization = <span>error</span>;
    if (loading) {
      personalization = <CircularProgress className={classes.progress} />;
    } else if (contentItem) {
      personalization = contentItem.personalizationDetails
        .isUsingPersonalization ? (
        <span>YES</span>
      ) : (
        <span>NO</span>
      );
    }

    let detailsLink = null;
    if (loading) {
      detailsLink = <CircularProgress className={classes.progress} />;
    } else if (contentItem) {
      detailsLink = (
        <Link to={`/page/${contentItem.id}/${node.data.id}`}>
          <IconButton
            color="secondary"
            className={classes.button}
            component="span"
          >
            <PageDetailsIcon />
          </IconButton>
        </Link>
      );
    }

    const { top, right, left, bottom } = params.transform;

    const arrowAreaStyle = {
      width: 350,
      height: 25,
      position: "relative",
      left: "0",
      bottom: 0
    };

    const arrowStyle = {
      width: 0,
      height: 0,
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      borderTop: "10px solid #000",
      position: "relative",
      left: "50%",
      bottom: 0
    };

    const radius = 100;

    const globalHeader = document.getElementsByClassName("sc-globalHeader");
    let globalHeaderHeight = 0;
    if (globalHeader && globalHeader.length > 0) {
      globalHeaderHeight = globalHeader[0].getBoundingClientRect().height;
    }

    const style = {
      zIndex: 1300,
      position: "absolute",
      right: right,
      left: left - 175,
      top: top - popoverHeight - radius - globalHeaderHeight,
      bottom: bottom + 10,
      width: "350px"
    };

    const pathParts = node.data.pageName.split("/");
    const pageName = pathParts[pathParts.length - 1];

    return (
      <div
        style={style}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <Paper className={classes.root}>
          <Typography component="div" className={classes.container}>
            <Grid container direction="row" spacing={0}>
              <Grid item xs={10}>
                <Typography variant="title" component="h3">
                  {pageName}
                </Typography>
                <Typography variant="caption" component="code" gutterBottom>
                  {node.data.path}
                </Typography>
                <Typography variant="caption" component="code" gutterBottom>
                  <span
                    style={{
                      verticalAlign: "top",
                      paddingRight: 5,
                      lineHeight: 1.7
                    }}
                  >
                    {this.getCategoryName(node.data)} type
                  </span>
                  <CategoryTypeIndicator fill={params.fill} />
                </Typography>
              </Grid>
              <Grid item xs={2} className={classes.detailButtonContainer}>
                {detailsLink}
              </Grid>
            </Grid>
            <Divider />
            <Grid
              container
              direction="row"
              spacing={8}
              style={{ paddingTop: 10 }}
            >
              <Grid item xs={4}>
                <Typography variant="title" component="h3">
                  {numeral(node.data.visits).format("0,0")}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Visits
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography
                  variant="caption"
                  component="code"
                  className={classes.trendLabel}
                >
                  last 30 days
                </Typography>
                <Sparklines
                  data={[10, 8, 8, 7, 5, 4, 4]}
                  limit={5}
                  width={100}
                  height={20}
                  margin={5}
                >
                  <SparklinesLine color="#808080" />
                  <SparklinesSpots style={{ fill: "#FF0000" }} />
                </Sparklines>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={8}
              style={{ paddingBottom: 10 }}
            >
              <Grid item xs={4}>
                <Typography variant="title" component="h3">
                  {numeral(node.data.value / node.data.visits).format("0,0")}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Value per visit
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Sparklines
                  data={[1, 1.2, 1.3, 1.6, 2]}
                  limit={5}
                  width={100}
                  height={20}
                  margin={5}
                >
                  <SparklinesLine color="#808080" />
                  <SparklinesSpots style={{ fill: "#FF0000" }} />
                </Sparklines>
              </Grid>
            </Grid>
            <Divider />
            <Grid
              container
              direction="row"
              spacing={8}
              style={{ paddingTop: 10 }}
            >
              <Grid item xs={6}>
                <Typography variant="title" component="h3">
                  {node.data.valueIndex === undefined
                    ? 0
                    : node.data.valueIndex}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Value index
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="title" component="h3">
                  {node.data.visitIndex === undefined
                    ? 0
                    : node.data.visitIndex}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Visit index
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="title" component="h3">
                  {personalization}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Personalized?
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="title" component="h3">
                  {numeral(3).format("0,0")}
                </Typography>
                <Typography className={classes.gutterBottom} component="p">
                  Recommendations
                </Typography>
              </Grid>
            </Grid>
          </Typography>
        </Paper>
        <div style={arrowAreaStyle}>
          <div style={arrowStyle} />
        </div>
      </div>
    );
  }
}

NodePopover.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(NodePopover);



// WEBPACK FOOTER //
// ./src/components/NodePopover.js